<template>
  <div class="payment-methods-card p-6 pt-8">
    <!-- Credit Card -->
    <div v-if="$hasAnyFeature('stripe', 'paytabs', 'checkout')" class="credit-card">
      <div class="flex items-center">
        <vs-radio
          id="fund-credit-card-radio-button"
          v-on:input="selectCard"
          value="payment"
          vsValue="creditCard"
          class="radio-btn mr-6"
          :disabled="isPaymentDisabled"
        ></vs-radio>
        <label
          @click="$gtag.event('Radio_CreditCard')"
          id="fund-credit-card-label"
          for="fund-credit-card-radio-button"
          class="title whitespace-no-wrap"
          :class="{ 'disabled-payments': isPaymentDisabled }"
          >Credit Card</label
        >
        <div class="w-full flex justify-end">
          <img
            class="w-16 md:w-32"
            :class="{ 'disabled-image': isPaymentDisabled }"
            src="@/assets/images/fund/credit-card.svg"
            alt="credit card"
          />
        </div>
      </div>
      <div
        class="description mt-2 mx-12"
        :class="{ 'disabled-payments': isPaymentDisabled }"
      >
        Enable automatic payment using your credit card
      </div>

      <!-- Credit Card form section -->
      <div v-if="creditCardSelected"
        class="w-full flex flex-wrap mt-4 md:mt-8 md:justify-between"
      >
        <div class="grid md:grid-cols-2 gap-4" style="width: 100%;">
          <!-- Cardholder name -->
          <div>
            <speakol-input
              id="fund-credit-card-cardholder-name-input"
              name="card_name"
              label="Cardholder name"
              v-model="card_name"
              placeholder="Cardholder name"
              validation="required|alpha_spaces"
              ref="cardHolder"
            ></speakol-input>
          </div>
          <div>
            <!-- Amount -->
            <speakol-input
              id="fund-credit-card-amount-input"
              label="Amount"
              v-model="amount"
              placeholder="Amount"
              :maxLength="creditCardsConfig.maxDigits"
              :validation="creditCardsConfig.amountValidation"
              ref="creditCardAmount"
            ></speakol-input>

            <span
              v-if="hasFirstPaymentError"
              class="error-message-span text-danger"
            >
              {{ $t("You can only spend up to $1000 until you complete the") }}
              <router-link
                class="account-details-link shadow-none"
                :to="'/settings/account?ref=status-bar&section=verification'"
              >
                <strong id="fund-creditCardAmount-input-validation-errors-actions" class="text-danger strong-underline">Verification Section</strong>
              </router-link>
            </span>
            <span
              v-if="hasAccountVerifiedError"
              class="error-message-span text-danger"
            >
              {{
                $t(
                  "The sum of all funds should not exceed $1,000 before completing the"
                )
              }}
              <router-link
                class="account-details-link shadow-none"
                :to="'/settings/account?ref=status-bar&section=verification'"
              >
                <strong id="fund-creditCardAmount-input-validation-errors-actions" class="text-danger strong-underline">Verification Section</strong>
              </router-link>
            </span>
          </div>

          <!-- Card details -->
          <div v-if="paymentMethod === $constants.PAYMENT_METHODS.STRIPE || paymentMethod === $constants.PAYMENT_METHODS.CHECKOUT">
            <div class="mr-2 mb-2 label">Card Details</div>
            <StripeCardInput
              v-if="paymentMethod === $constants.PAYMENT_METHODS.STRIPE"
              v-on:payment-succeeded="showSuccessPopup"
              v-on:payment-failed="showFailPopup"
              ref="stripe-card"
            >
            </StripeCardInput>

            <CheckoutCardInput
              v-if="paymentMethod === $constants.PAYMENT_METHODS.CHECKOUT"
              v-on:payment-succeeded="showSuccessPopup"
              v-on:payment-failed="showFailPopup"
              :cardHolderName="card_name"
              ref="checkout-card"
            >
            </CheckoutCardInput>
          </div>
          <div v-if="paymentMethod === $constants.PAYMENT_METHODS.STRIPE || paymentMethod === $constants.PAYMENT_METHODS.CHECKOUT"></div>
          <!-- Button -->
          <div>
            <vs-button
              id="fund-crdit-card-pay-button"
              :disabled="disablePayButton"
              @click="payWithCreditCard"
              class="w-full md:btn"
              >pay</vs-button
            >
          </div>
        </div>
      </div>
    </div>

    <vs-divider v-if="$hasAnyFeature('paypal')"></vs-divider>

    <!-- Paypal -->
    <div v-if="$hasFeature('paypal')" class="PayPal mt-6">
      <div class="flex items-center">
        <vs-radio
          id="fund-paypal-label"
          v-on:input="selectCard"
          value="payment"
          class="radio-btn mr-6"
          vsValue="paypal"
          :disabled="isPaymentDisabled"
        ></vs-radio>
        <label
          @click="$gtag.event('Radio_Paypal')"
          id="fund-paypal-label"
          for="fund-paypal-label"
          class="title whitespace-no-wrap"
          :class="{ 'disabled-payments': isPaymentDisabled }"
          >PayPal</label
        >
        <div class="w-full flex justify-end">
          <img class="w-16 md:w-32" :class="{ 'disabled-image': isPaymentDisabled }" src="@/assets/images/fund/paypal.svg" alt="paypal" />
        </div>
      </div>

      <div
        class="description mt-2 mx-12"
        :class="{ 'disabled-payments': isPaymentDisabled }"
      >
        {{ $t('Prepay your bill by making incremental PayPal payment') }}
      </div>

      <div v-if="paypalSelected">
        <!-- Amount -->
        <div class="mt-4 md:mt-8 flex flex-wrap">
          <!-- Amount -->
          <speakol-input
            label="Amount"
            v-model="amount"
            :maxLength="payPalConfig.maxDigits"
            :validation="payPalConfig.amountValidation"
            placeholder="Amount"
            class="md:input"
            ref="paypalAmount"
            correlationKey="stripe-amount"
            field="amount"
            id="fund-paypal-amount-input"
          ></speakol-input>

          <span
            v-if="hasFirstPaymentError"
            class="error-message-span text-danger"
          >
            {{ $t("You can only spend up to $1000 until you complete the") }}
            <router-link
              class="account-details-link shadow-none"
              :to="'/settings/account?ref=status-bar&section=verification'"
            >
              <strong class="text-danger strong-underline">Verification Section</strong>
            </router-link>
          </span>
          <span
            v-if="hasAccountVerifiedError"
            class="error-message-span text-danger"
          >
            {{
              $t(
                "The sum of all funds should not exceed $1,000 before completing the"
              )
            }}
            <router-link
              class="account-details-link shadow-none"
              :to="'/settings/account?ref=status-bar&section=verification'"
            >
              <strong class="text-danger strong-underline">Verification Section</strong>
            </router-link>
          </span>
        </div>
        <span class="text-danger text-sm block mt-2">{{
          errors.first("paypal amount")
        }}</span>

        <!-- Button -->
        <div class="w-full mt-4 md:mt-8 flex flex-wrap md:pr-2">
          <div class="w-full">
            <vs-button
              id="fund-paypal-pay-button"
              :disabled="disablePayButton"
              @click="payWithPayPal"
              class="w-full md:w-48 btn"
              >pay</vs-button
            >
          </div>
        </div>
      </div>
    </div>

    <speakol-response-popup
      v-on:hide-response-popup="popup.show = false"
      :responsePopupData="popup"
      @hide-response-popup="refreshPage()"
    />
  </div>
</template>

<script>
import StripeCardInput from './StripeCardInput.vue'
import {get} from 'lodash'
import CheckoutCardInput from './CheckoutCardInput.vue'

import '@/validators/AccountDetails'

export default {
  props: ['paymentMethod', 'total', 'disablePayButton'],

  data () {
    return {
      haveTransactions: true,
      creditCardSelected: false,
      paypalSelected: false,
      amount: null,
      card_name: '',
      popup: {}
    }
  },
  components: {
    StripeCardInput,
    CheckoutCardInput
  },
  computed: {
    isPaymentDisabled () {
      return this.$store.getters['authentication/activeAccount'].is_payment_disabled
    },
    hasFirstPaymentError () {
      return this.errors.items.some(el => el.rule === 'first_payment') && this.amount > 0
    },
    hasAccountVerifiedError () {
      return this.errors.items.some(el => el.rule === 'account_verified') && this.amount > 0
    },

    payPalConfig () {
      const minAmount = this.haveTransactions ? (process.env.VUE_APP_PAYPAL_FUND_MIN_AMOUNT || 100) : (process.env.VUE_APP_NEW_ACCOUNT_PAYPAL_FUND_MIN_AMOUNT || 1000)
      const maxAmount = this.haveTransactions ? (process.env.VUE_APP_PAYPAL_FUND_MAX_AMOUNT || 1000) : (process.env.VUE_APP_NEW_ACCOUNT_PAYPAL_FUND_MAX_AMOUNT || 1000)
      const maxDigits = maxAmount.toString().length
      const amountValidation = minAmount !== maxAmount ? `required|first_payment|account_verified|decimal|min_value:${minAmount}|max_value:${maxAmount}` : `required|first_payment|account_verified|decimal|is:${minAmount}`


      return {
        minAmount,
        maxAmount,
        maxDigits,
        amountValidation
      }
    },

    creditCardsConfig () {
      const minAmount = this.haveTransactions ? (process.env.VUE_APP_CARD_FUND_MIN_AMOUNT || 100) : (process.env.VUE_APP_NEW_ACCOUNT_CARD_FUND_MIN_AMOUNT || 1000)
      const maxAmount = this.haveTransactions ? (process.env.VUE_APP_CARD_FUND_MAX_AMOUNT || 10000) : (process.env.VUE_APP_NEW_ACCOUNT_CARD_FUND_MAX_AMOUNT || 10000)
      const maxDigits = maxAmount.toString().length
      const amountValidation = minAmount !== maxAmount ? `required|first_payment|account_verified|regex:^([0-9]+)$|min_value:${minAmount}|max_value:${maxAmount}` : `required|first_payment|account_verified|regex:^([0-9]+)$|is:${minAmount}`

      return {
        minAmount,
        maxAmount,
        maxDigits,
        amountValidation
      }
    }

  },
  async mounted () {
    this.haveTransactions = (await this.getTransactionsCount()) > 0

    const status = get(this, '$route.query.status')

    if (status === 'true') {
      // this line to fetch the account data to update the status banner
      this.$store.dispatch('authentication/getUserAuth', localStorage.getItem('activeAccountId'))
      this.setPopup(true, 'success', this.$t('advertisers_fund.fund.payment_succeeded'))
    } else if (status === 'false') {
      this.setPopup(true, 'error', this.$route.query.rejection_reason)
    }
  },
  methods: {

    addComma (num) {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    async getTransactionsCount () {
      const { data } = await window.axios.post('/payments/transactions')
      if (data.success) {
        return parseInt(data.payload.total)
      }
      return 0
    },
    setPopup (show = false, type = 'success', body = '') {
      this.popup = {
        show,
        type,
        body
      }
    },

    beautifyAPIError (error) {
      if (error.request.response) {
        return JSON.parse(error.request.response)
      }
      return error
    },

    selectCard (paymentMethod) {
      this.$gtag.event(`Radio_${paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1)}`)
      this.creditCardSelected = paymentMethod === 'creditCard'
      this.paypalSelected = paymentMethod === 'paypal'
      this.setDefaults()
      this.$emit('paymentMethodSelected', paymentMethod)
    },

    async payWithCreditCard () {
      let token = null
      this.$gtag.event('Pay_CreditCard')

      try {
        if (!await this.isValidPaymentPayload(this.paymentMethod)) {
          return 'validation failed'
        }

        if (this.paymentMethod === this.$constants.PAYMENT_METHODS.CHECKOUT) {
          token = (await this.$refs['checkout-card'].getToken())

          if (!token) {
            return 'failed to get token'
          }
        }

        await this.payWithMethodOfType(this.paymentMethod, this.total, this.card_name, token)
      } catch (error) {
        this.showFailPopup(error)
      }
    },

    async isValidPaypal () {
      return await this.$refs['paypalAmount'].validate()
    },

    async isValidCreditCard () {
      return await this.isValidMainInputs() && this.errors.items.length === 0
    },

    async isValidMainInputs () {
      const inputs = await Promise.all([this.$refs['cardHolder'].validate(), this.$refs['creditCardAmount'].validate()])

      return inputs[0] && inputs[1]
    },


    async isValidPaymentPayload (method) {
      this.errors.clear()
      if (method === this.$constants.PAYMENT_METHODS.PAYPAL) {
        return await this.isValidPaypal()
      } else if (method === this.$constants.PAYMENT_METHODS.PAYTABS) {
        return await this.isValidMainInputs()
      }
      return await this.isValidCreditCard()
    },

    async payWithMethodOfType (method, amount, cardName = null, token = null) {
      try {
        const {data: response} = await this.sendFundRequest(method, amount, cardName, token)

        if (method === this.$constants.PAYMENT_METHODS.STRIPE) {
          await this.confirmStripePayment(response)
        }

        if (response.data.redirect_url) {
          window.location.href = response.data.redirect_url
        }
      } catch (error) {
        this.showFailPopup(error)
      }
    },

    async sendFundRequest (method, amount, cardName = null, token = null) {
      const { ref } = this.$route.query
      try {
        return await window.axios.post('/payments/fund',
          {
            cardName,
            amount,
            token,
            ref,
            'payment-method': method
          }
        )
      } catch (error) {
        this.showFailPopup(error)
      }
    },

    async payWithPayPal () {
      this.$gtag.event('Pay_Paypal')

      if (!await this.isValidPaymentPayload(this.paymentMethod)) {
        return 'paypal validation failed'
      }

      await this.payWithMethodOfType(this.$constants.PAYMENT_METHODS.PAYPAL, this.total)
    },

    async confirmStripePayment (paymentFirstStepResponse) {
      try {
        await this.$refs['stripe-card'].confirmPayment(paymentFirstStepResponse)
      } catch (error) {
        this.showFailPopup(error)
      }
    },

    setDefaults () {
      this.setPopup()

      this.amount = null
      this.card_name = ''
      this.$validator.reset()
      this.$emit('amount-change', this.amount)
    },

    refreshPage () {
      this.$gtag.event('Payment_popup_confirmation')

      if (this.errors.items.length === 0 && this.paymentMethod === this.$constants.PAYMENT_METHODS.STRIPE) {
        this.$router.go()
      } else {
        this.$router.push('/fund')
      }
    },

    showSuccessPopup () {
      this.setPopup(true, 'success', this.$t('advertisers_fund.fund.payment_succeeded'))
    },

    showFailPopup (error) {
      if (error.code) {
        this.popup.body = `${error.message} ${this.$t('advertisers_fund.fund.validation.contact_bank')}`
      } else {
        this.popup.body = this.beautifyAPIError(error)
      }

      this.popup.show = true
    }
  },
  watch: {
    amount () {
      this.$emit('amount-change', this.amount)
    }
  }
}
</script>

<style lang="scss" scoped>
.error-message-span {
  //color: #db2828;
  font-size: 0.875rem !important;
}

.strong-underline {
  text-decoration: underline;
}

.payment-methods-card {
  background: #fff;
  border: 1px solid #f2f2f2;
  border-radius: 15px;
}

.title {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #2c2c2c;
}

.description {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 21px;
  color: #999;
}

.radio-btn {
  width: 15px;
  height: 15px;
}

.label {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 21px;
  color: #2c2c2c;
}

.btn {
  background: #0092d7;
  border-radius: 3px;
}

@media (min-width: 768px) {
  .md\:input {
    width: calc(50% - 15px) !important;
  }

  .md\:btn {
    width: 135px !important;
    height: 40px !important;
    border-radius: 3px !important;
  }
}

.disabled-payments {
  color: #e0e0e0 !important;
}

.disabled-image {
  filter: grayscale(100%);
  opacity: 0.5;
}

</style>
